import type { StrategyNode } from '../types/BaseNode';

/**
 * Immutably updates a parameter in the strategy tree (works on Plain Objects).
 * Avoids prototype loss by not relying on node.update() methods.
 */
export function updateNodeParam(
    node: StrategyNode | null,
    nodeId: string,
    paramKey: string,
    newValue: number | boolean | string
): StrategyNode | null {
    if (!node) return null;

    // 1. If this is the node we're looking for, update its props
    if (node.id === nodeId) {
        if ((node.type === 'Single' || node.type === 'Formatter') && 'config' in node && 'props' in node.config) {
            return {
                ...node,
                isManualRename: node.isManualRename,
                config: {
                    ...node.config,
                    props: {
                        ...(node.config.props as Record<string, any>),
                        [paramKey]: newValue,
                    }
                }
            } as StrategyNode;
        }
        // Composites don't have "props" to tune in this context
        return node;
    }

    // 2. Otherwise, recurse deeper
    if (node.type === 'Composite' && 'config' in node) {
        const left = node.config.left ? updateNodeParam(node.config.left, nodeId, paramKey, newValue) : undefined;
        const right = node.config.right ? updateNodeParam(node.config.right, nodeId, paramKey, newValue) : undefined;

        // Only create new object if children changed
        if (left !== node.config.left || right !== node.config.right) {
            return {
                ...node,
                isManualRename: node.isManualRename,
                config: {
                    ...node.config,
                    left: left || undefined, // explicit undefined for optional fields
                    right: right || undefined,
                }
            } as StrategyNode;
        }
    } else if (node.type === 'Formatter' && 'config' in node && node.config.child) {
        const child = updateNodeParam(node.config.child, nodeId, paramKey, newValue);
        if (child !== node.config.child) {
            return {
                ...node,
                isManualRename: node.isManualRename,
                config: {
                    ...node.config,
                    child: child || null, // check if child is nullable in definition, safely cast or assume strictly typed structure
                }
            } as StrategyNode;
        }
    }

    return node;
}
